import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useOfflineProducts } from "@/hooks/useOfflineProducts";
import { useOfflineCategories } from "@/hooks/useOfflineCategories";
import { ImportMatchItem } from "./import-list-types";
import Select from "react-select";
import { Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { addShoppingItem } from "@/services/shopping/items";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth/AuthContext";
import { useModalBackButton } from "@/hooks/useModalBackButton";
import { safeToastError } from "@/components/ui/toastManager";
import { validateQuantity } from "@/utils/security";

interface CreateProductsDialogProps {
  open: boolean;
  items: ImportMatchItem[];
  listId: string;
  onComplete: () => void;
  onCancel: () => void;
}

interface NewProductRow {
  name: string;
  categoryId: string;
  matchId: string | null;
}

const CreateProductsDialog = ({ open, items, listId, onComplete, onCancel }: CreateProductsDialogProps) => {
  const { theme } = useThemeConfig();
  const { products, createProduct } = useOfflineProducts();
  const { categories } = useOfflineCategories();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const cancelRef = useRef(true);
  const { handleOpenChange } = useModalBackButton({
    isOpen: open,
    modalName: "create-products-dialog",
    onOpenChange: (o: boolean) => {
      if (!o && cancelRef.current) {
        onCancel();
      }
      cancelRef.current = true;
    }
  });

  const [rows, setRows] = useState<NewProductRow[]>([]);

  useEffect(() => {
    if (open) {
      setRows(
        items.map(i => {
          const match = products.find(p => p.name.toLowerCase() === i.original.toLowerCase());
          return {
            name: i.original,
            categoryId: categories[0]?.id || "",
            matchId: match ? match.id : null,
          };
        })
      );
    }
  }, [open, items, products, categories]);

  const categoryOptions = categories.map(c => ({ value: c.id, label: c.name }));

  const updateName = (index: number, name: string) => {
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const match = products.find(p => p.name.toLowerCase() === name.toLowerCase());
        return { ...r, name, matchId: match ? match.id : null };
      })
    );
  };

  const updateCategory = (index: number, categoryId: string) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, categoryId } : r)));
  };

  const handleExecute = async () => {
    try {
      for (const row of rows) {
        let productId = row.matchId;
        let unit = "pz";

        if (!productId) {
          const created = await createProduct({
            userId: user?.id || localStorage.getItem("userId") || "",
            name: row.name,
            defaultQuantity: 1,
            defaultUnit: "pz",
            categoryId: row.categoryId,
            attributes: [],
          });
          productId = created?.id;
          unit = created?.defaultUnit || unit;
        } else {
          const existing = products.find(p => p.id === productId);
          unit = existing?.defaultUnit || unit;
        }

        if (productId) {
          const quantityResult = validateQuantity('1');

          if (!quantityResult.isValid) {
            safeToastError("Quantità non valida per l'articolo creato");
            continue;
          }

          await addShoppingItem(listId, {
            product_id: productId,
            quantity: quantityResult.sanitized,
            unit,
            category: row.categoryId,
          });
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["shopping-items", listId] });
      cancelRef.current = false;
      onComplete();
      handleOpenChange(false);
    } catch (e) {
      safeToastError("Errore durante la creazione dei prodotti");
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[50vh] overflow-y-auto"
        style={{
          borderRadius: theme.borderRadius || "0.5rem",
          backgroundColor: theme.cardBackgroundColor || theme.backgroundColor,
          color: theme.sectionTextColor || theme.textColor,
        }}
      >
        <DialogHeader className="space-y-2 pb-4">
          <DialogTitle>Crea Prodotti</DialogTitle>
          <DialogDescription style={{ color: theme.sectionTitleColor || "#000000" }}>
            Seleziona le categorie per i nuovi prodotti
          </DialogDescription>
        </DialogHeader>
        <div role="table" className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} role="row" className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <Input
                value={row.name}
                onChange={e => updateName(i, e.target.value)}
                style={{
                  backgroundColor: theme.backgroundColor,
                  color: theme.textColor,
                  borderColor: theme.borderColor,
                }}
              />
              <Select
                options={categoryOptions}
                value={categoryOptions.find(o => o.value === row.categoryId) || null}
                onChange={opt => updateCategory(i, opt?.value || "")}
                placeholder="Categoria..."
                styles={{
                  control: base => ({
                    ...base,
                    minHeight: "32px",
                    backgroundColor: theme.backgroundColor,
                    color: theme.textColor,
                    borderColor: theme.borderColor,
                    fontSize: "0.875rem",
                  }),
                  menu: base => ({ ...base, backgroundColor: theme.backgroundColor }),
                }}
              />
              {row.matchId ? (
                <Check className="h-4 w-4" style={{ color: theme.successColor }} aria-label="Abbinato" />
              ) : (
                <X className="h-4 w-4" style={{ color: theme.errorColor }} aria-label="Non abbinato" />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-4">
          <Button
            onClick={handleCancel}
            variant="outline"
            style={{
              backgroundColor: theme.backgroundColor,
              color: theme.textColor,
              borderColor: theme.inputBorderColor,
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding,
              fontWeight: theme.buttonFontWeight,
              transitionDuration: theme.transitionSpeed,
              '--btn-bg-hover': theme.primaryColor,
            } as React.CSSProperties}
          >
            Annulla
          </Button>
          <Button
            onClick={handleExecute}
            className="hover:bg-[var(--btn-bg-hover)]"
            style={{
              backgroundColor: theme.primaryColor,
              color: theme.textPrimaryColor,
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding,
              fontWeight: theme.buttonFontWeight,
              transitionDuration: theme.transitionSpeed,
              '--btn-bg-hover': theme.primaryColorDark || theme.primaryColor,
            } as React.CSSProperties}
          >
            Esegui
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProductsDialog;
